import { useState, type FormEvent } from 'react'
import { cleanVatRate, formatEur, formatQty, parsePriceCents, parseQty, taxLabel, type InventoryPartDTO, type PartDTO } from '@wf/shared'
import { barcodeScanSupported, useBarcodeScanner } from '../parts/useBarcodeScanner'
import { useInventorySearch } from '../inventory/useInventory'
import { LineEditForm } from './LineEditForm'
import { VatSelect } from './VatSelect'
import { useAddPart, useDeletePart, usePatchPart } from './useOrders'

const PART_STATUS_LABEL: Record<string, string> = {
  needed: 'Benötigt',
  ordered: 'Bestellt',
  delivered: 'Geliefert',
  installed: 'Verbaut',
}
const PART_STATUSES = ['needed', 'ordered', 'delivered', 'installed']

/** Teile-Liste eines Auftrags mit Status (Benötigt → Verbaut), Anlegen und Entfernen. Eingabe: Bezeichnung, SNR,
 *  Verkaufspreis (netto), MwSt, Anzahl. Beim Tippen in Bezeichnung ODER SNR werden Inventarteile vorgeschlagen
 *  (Übernahme reserviert Bestand + snapshottet Preis/SNR/MwSt). Kamera-Scan füllt die SNR. Geld ausschließlich
 *  über @wf/shared (formatEur/parsePriceCents) — ganze Cent, nie Float-Euro. */
export function OrderParts({ orderId, parts, canManage }: { orderId: number; parts: PartDTO[]; canManage: boolean }) {
  const add = useAddPart(orderId)
  const patch = usePatchPart(orderId)
  const remove = useDeletePart(orderId)
  const [name, setName] = useState('')
  const [partNumber, setPartNumber] = useState('')
  const [price, setPrice] = useState('')
  const [vatRate, setVatRate] = useState(19)
  const [qty, setQty] = useState('1')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [scanning, setScanning] = useState(false)

  // Live-Suche im Inventar: matcht Bezeichnung ODER SNR (ab 2 Zeichen). Treffer kann man direkt übernehmen.
  const searchTerm = name.trim().length >= 2 ? name.trim() : partNumber.trim().length >= 2 ? partNumber.trim() : ''
  const search = useInventorySearch(searchTerm)
  const matches = searchTerm ? (search.data?.parts ?? []) : []

  // Kamera-Scan (zxing) → die gescannte Nummer landet in der SNR; die Live-Suche zeigt passende Inventarteile.
  const scanner = useBarcodeScanner(scanning, (code) => {
    setScanning(false)
    setPartNumber(code.trim())
  })

  function resetAdd() {
    setName('')
    setPartNumber('')
    setPrice('')
    setVatRate(19)
    setQty('1')
  }

  // Einmal-Teil (frei eingetippt): kein Lagerbezug, läuft über den order_oneoff-Bestellprozess.
  function submitAdd(event: FormEvent) {
    event.preventDefault()
    if (!name.trim()) return
    add.mutate(
      {
        name: name.trim(),
        partNumber: partNumber.trim() || null,
        priceCents: parsePriceCents(price),
        vatRate: cleanVatRate(vatRate),
        qty: parseQty(qty),
      },
      { onSuccess: resetAdd },
    )
  }

  // Inventarteil übernehmen: Bestand reservieren + Preis/Bezeichnung/SNR/MwSt als Snapshot (Backend via partId).
  function addInventory(part: InventoryPartDTO) {
    add.mutate({ name: part.name, partId: part.id, qty: parseQty(qty) }, { onSuccess: resetAdd })
  }

  return (
    <section className="wf-lines">
      <span className="wf-label wf-sectiontitle">Teile</span>
      <div className="wf-linelist">
        {parts.map((part) =>
          editingId === part.id ? (
            <LineEditForm
              key={part.id}
              label="Teil"
              busy={patch.isPending}
              withPartNumber
              initial={{ name: part.name, partNumber: part.partNumber, priceCents: part.priceCents, vatRate: part.vatRate, qty: part.qty }}
              onSave={(draft) => patch.mutate({ partId: part.id, body: draft }, { onSuccess: () => setEditingId(null) })}
              onCancel={() => setEditingId(null)}
            />
          ) : (
            <div key={part.id} className="wf-line">
              <div className="wf-line__main">
                <div className="wf-line__name">
                  {part.name}
                  {part.partNumber ? <span className="wf-line__sub"> · {part.partNumber}</span> : null}
                </div>
                <div className="wf-line__sub">
                  {part.qty}× {formatEur(part.priceCents)} · {taxLabel(part.vatRate)} ={' '}
                  <span className="wf-tnum">{formatEur(part.priceCents * part.qty)}</span>
                </div>
              </div>
              {canManage ? (
                <select
                  className="wf-input wf-line__status"
                  aria-label={`Status: ${part.name}`}
                  value={part.status}
                  disabled={patch.isPending}
                  onChange={(event) => patch.mutate({ partId: part.id, body: { status: event.target.value } })}
                >
                  {PART_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {PART_STATUS_LABEL[status] ?? status}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="wf-line__sub">{PART_STATUS_LABEL[part.status] ?? part.status}</span>
              )}
              {canManage ? (
                <button type="button" className="wf-iconbtn" aria-label={`Teil bearbeiten: ${part.name}`} onClick={() => setEditingId(part.id)}>
                  ✎
                </button>
              ) : null}
              {canManage ? (
                <button
                  type="button"
                  className="wf-iconbtn"
                  aria-label={`Teil entfernen: ${part.name}`}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(part.id)}
                >
                  ✕
                </button>
              ) : null}
            </div>
          ),
        )}
        {parts.length === 0 ? <div className="wf-line__sub">Noch keine Teile</div> : null}
      </div>
      {canManage ? (
        <div>
          <form className="wf-line-add" onSubmit={submitAdd}>
            <input
              className="wf-input"
              placeholder="Bezeichnung"
              aria-label="Teil-Bezeichnung"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <input className="wf-input" placeholder="SNR" aria-label="Teil-SNR" value={partNumber} onChange={(e) => setPartNumber(e.target.value)} />
            <input className="wf-input wf-tnum" placeholder="€ netto" inputMode="decimal" aria-label="Teil-Nettopreis" value={price} onChange={(e) => setPrice(e.target.value)} />
            <VatSelect value={vatRate} onChange={(r) => setVatRate(cleanVatRate(r))} label="Teil-MwSt" />
            <input className="wf-input wf-tnum wf-line__qty" inputMode="numeric" aria-label="Teil-Menge" value={qty} onChange={(e) => setQty(e.target.value)} />
            <button type="submit" className="wf-btn wf-btn--ghost" disabled={add.isPending || !name.trim()}>
              Teil
            </button>
            {barcodeScanSupported() ? (
              <button type="button" className="wf-btn wf-btn--ghost" onClick={() => setScanning(true)}>
                Scannen
              </button>
            ) : null}
          </form>
          {scanner.error ? (
            <p className="error" role="alert">
              {scanner.error}
            </p>
          ) : null}
          {scanning ? (
            <div className="wf-scanner">
              <video ref={scanner.videoRef} className="wf-scanner__video" muted playsInline aria-label="Kamera-Sucher" />
              <button type="button" className="wf-btn wf-btn--ghost" onClick={() => setScanning(false)}>
                Abbrechen
              </button>
            </div>
          ) : null}
          {matches.length > 0 ? (
            <div className="wf-linelist">
              <span className="wf-line__sub">Im Inventar gefunden – übernehmen (Bestand wird reserviert):</span>
              {matches.map((part) => (
                <div key={part.id} className="wf-line">
                  <span className="wf-line__main">
                    <span className="wf-line__name">{part.name}</span>
                    <span className="wf-line__sub wf-tnum">
                      {part.partNumber} · {formatEur(part.sellingPriceCents)} · {taxLabel(part.vatRate)} · Lager {formatQty(part.stockTenths)} {part.unit}
                    </span>
                  </span>
                  <button type="button" className="wf-btn wf-btn--ghost wf-btn--sm" disabled={add.isPending} onClick={() => addInventory(part)}>
                    Übernehmen
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}
